import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  emptyAlarmNodeData,
  updateAlarmNodeSelectionData,
  updateAlarmNodeSelectionState,
  updateIsBlueprintApplied,
  updateNodeManagerActiveView,
  updateShowUnsavedStructureModal,
  updateStructureWasSaved,
} from "@src/reducers/your-reducer";
import { RootState } from "@src/reducers/store";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import clearInitialAndMachineStructure from "@src/cloud-connect/node-manager/clear-initial-and-machine-structures";
import { transferConfigurationToClient } from "@src/cloud-connect/node-manager/transfer-configuration-to-client/transfer-configuration-to-client";
import { transferAlarmProfilesToClientUseCase } from "@src/cloud-connect/alarms/use-cases/transfer-alarm-profiles-to-client";
import {
  getAlarmProfiles,
  getAlarmProfilesReferenceCopy,
} from "@src/cloud-connect/dependency-injection-container/get-alarm-profiles";

export const useUnsavedStructureModalHook = () => {
  const [saveStructureLoading, setSaveStructureLoading] = useState(false);
  const dispatch = useDispatch();

  const nodeManagerActiveView = useSelector(
    (state: RootState) => state.yourSlice.nodeManagerActiveView,
  );

  const routeToNavigateAfterConfirmation = useSelector(
    (state: RootState) =>
      state.yourSlice.unsavedStructureModal.routeToNavigateAfterConfirmation,
  );

  const setPillNavToTreeStructure = () => {
    dispatch(updateNodeManagerActiveView("treeStructure"));
  };

  const flushAlarmRowSidePanelInfo = () => {
    dispatch(updateAlarmNodeSelectionState("none"));
    dispatch(updateAlarmNodeSelectionData(emptyAlarmNodeData));
  };

  const clearAlarmProfiles = () => {
    const alarmProfiles = getAlarmProfiles();
    alarmProfiles.setAlarmProfiles([]);

    const alarmProfilesReferenceCopy = getAlarmProfilesReferenceCopy();
    alarmProfilesReferenceCopy.setAlarmProfiles([]);
  };

  const exitWithoutSaving = () => {
    closeModal();
    navigateTo(routeToNavigateAfterConfirmation);
    setPillNavToTreeStructure();
    dispatch(updateStructureWasSaved(true));
    clearInitialAndMachineStructure();
    dispatch(updateIsBlueprintApplied(false));
    flushAlarmRowSidePanelInfo();
    clearAlarmProfiles();
  };

  const isItTreeStructureView = () => {
    return nodeManagerActiveView === "treeStructure";
  };

  const saveAndExit = async () => {
    if (isItTreeStructureView()) {
      // the boolean value is setting false to overwriteReduxStateWithLastSavedStructure
      // not needed when going back to the sources list page
      await transferConfigurationToClient(false, setSaveStructureLoading);
      clearInitialAndMachineStructure();
    } else {
      await transferAlarmProfilesToClientUseCase(setSaveStructureLoading);
    }
    setPillNavToTreeStructure();
    flushAlarmRowSidePanelInfo();
    clearAlarmProfiles();
    closeModal();
    navigateTo(routeToNavigateAfterConfirmation);
  };

  const closeModal = () => {
    dispatch(
      updateShowUnsavedStructureModal({
        showUnsavedStructureModal: false,
        routeToNavigateAfterConfirmation: Route.MACHINES,
      }),
    );
  };

  return {
    saveStructureLoading,
    closeModal,
    saveAndExit,
    exitWithoutSaving,
  };
};
